<template>
  <q-dialog v-model="showModal" @hide="onHide">
    <div class="campaign-modal">

      <!-- HEADER -->
      <div class="campaign-modal__header">
        <q-linear-progress :value="1" class="bg-primary" />
        <q-toolbar class="bg-primary text-white">
          <q-toolbar-title class="text-h6">
            {{ mode === 'edit' ? $gettext('EDIT_CAMPAIGN') : $gettext('NEW_CAMPAIGN') }}
          </q-toolbar-title>
          <q-btn flat round dense icon="close" @click="onCloseButtonClick" />
        </q-toolbar>
      </div>

      <!-- BODY -->
      <q-form ref="campaignForm" @submit.prevent="onSave" class="campaign-modal__body">
        <div class="campaign-container row q-col-gutter-md">

          <!-- LEFT -->
          <div class="campaign-col-left col-12 col-md-6">
            <!-- Informações básicas -->
            <q-card flat class="q-mb">
              <q-card-section class="q-py-sm q-card__section q-card__section--vert">
                <div class="crud-title row items-center justify-between">
                  <span>{{ $gettext('BASIC_INFORMATION') }}</span>
                </div>
              </q-card-section>
              <q-card-section class="section-body">
                <div class="row q-col-gutter-md">
                  <div class="col-12">
                    <q-input
                      ref="inputCampaignName"
                      v-model="formData.name"
                      outlined
                      :label="$gettext('CAMPAIGN_NAME') + ' *'"
                      campaign.recurrence    :rules="[val => !!val || $gettext('REQUIRED_FIELD')]"
                    />
                  </div>
                  <div :class="mode === 'edit' ? 'col-9' : 'col-12'">
                    <q-select
                      campaign.recurrence   ref="inputCampaignTemplate"
                      v-model="formData.template"
                      outlined
                      :label="$gettext('TEMPLATE') + ' *'"
                      :options="templateOptions"
                      option-value="value"
                      option-label="label"
                      emit-value
                      map-options
                      :rules="[val => !!val || $gettext('REQUIRED_FIELD')]"
                      @input="onTemplateChange"
                    />
                  </div>
                  <div v-if="mode === 'edit'" class="col-3">
                    <q-select
                      ref="inputCampaignStatus"
                      v-model="formData.status"
                      outlined
                      :label="$gettext('STATUS') + ' *'"
                      :options="statusOptions"
                      option-value="value"
                      option-label="label"
                      emit-value
                      map-options
                      :rules="[val => !!val || $gettext('REQUIRED_FIELD')]"
                    />
                  </div>
                </div>
              </q-card-section>
            </q-card>
            <!-- Informações básicas -->
            <q-card flat class="q-mb">
              <q-card-section class="q-py-sm q-card__section q-card__section--vert">
                <div class="crud-title row items-center justify-between">
                  <span>{{ $gettext('SEGMENTATION_FILTER') }}</span>
                </div>
              </q-card-section>
              <q-card-section class="section-body">
                <div class="row q-col-gutter-md">
                  <div class="col-12">
                    <q-select
                      ref="inputCampaignConcessionTariff"
                      v-model="formData.concessionContractTariff"
                      outlined
                      :label="$gettext('CONCESSION_CONTRACT_TARIFF') + ' *'"
                      :options="concessionContractTariffOptions"
                      option-value="value"
                      option-label="label"
                      emit-value
                      map-options
                      :clearable="!concessionContractTariffReadOnly"
                      :disable="concessionContractTariffReadOnly"
                      :rules="[val => !!val || $gettext('REQUIRED_FIELD')]"
                    />
                  </div>
                  <div class="col-12">
                    <q-select
                      ref="inputNeighborhoodSelect"
                      v-model="formData.neighborhood"
                      outlined
                      :label="$gettext('NEIGHBORHOOD')"
                      :options="neighborhoodOptions"
                      option-value="value"
                      option-label="label"
                      emit-value
                      map-options
                      clearable
                    />
                  </div>
                </div>
              </q-card-section>
            </q-card>
            <!-- Scheduling -->
            <q-card flat class="q-mb">
              <q-card-section class="q-py-sm q-card__section q-card__section--vert">
                <div class="crud-title">
                  <div>
                    <span>{{ $gettext('SCHEDULING') }}</span>
                  </div>
                </div>
              </q-card-section>
              <q-card-section class="section-body">
                <div class="row q-col-gutter-md">
                  <div class="col-12">
                    <q-checkbox v-model="recurringToggle" :disable="isRegularOrInstallmentInvoicePurpose">
                      <template v-slot:default>
                        <span class="row items-center q-gutter-xs">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #707070;" aria-hidden="true">
                            <path d="m17 2 4 4-4 4"></path>
                            <path d="M3 11v-1a4 4 0 0 1 4-4h14"></path>
                            <path d="m7 22-4-4 4-4"></path>
                            <path d="M21 13v1a4 4 0 0 1-4 4H3"></path>
                          </svg>
                          <span>{{ $gettext('CAMPAIGN_RECURRING') }}</span>
                        </span>
                      </template>
                    </q-checkbox>
                      <div v-if="isRecurring" class="row q-col-gutter-md q-mt-xs q-ml-sm col-md-1" style="border-left: 2px solid rgba(14,136,228,0.2); padding-left: 12px;">
                      <div class="col-12 col-md-6">
                        <q-select
                          v-model="formData.recurrenceType"
                          outlined
                          :label="$gettext('RECURRENCE_TYPE')"
                          :options="recurrenceTypeOptions"
                          option-value="value"
                          option-label="label"
                          emit-value
                          map-options
                          clearable
                        />
                      </div>
                      <div v-if="isCustomRecurrence" class="col-12 col-md-6">
                        <q-input
                          v-model.number="formData.customInterval"
                          outlined
                          type="number"
                          :label="$gettext('CUSTOM_INTERVAL')"
                          :hint="$gettext('CUSTOM_INTERVAL_HINT')"
                          :disable="isRegularOrInstallmentInvoicePurpose"
                        />
                      </div>
                      <div v-if="isRecurring && !isCustomRecurrence && !isAdditionalPurpose" class="col-12 col-md-6">
                        <q-input
                          v-model.number="formData.leadTimeDays"
                          @input="onLeadTimeDaysInput"
                          outlined
                          type="number"
                          min="0"
                          max="15"
                          :label="$gettext('LEAD_TIME_DAYS')"
                          :hint="$gettext('LEAD_TIME_DAYS_HINT')"
                          :disabled="false"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="col-12 col-md-6">
                    <q-input
                      ref="inputCampaignStartDate"
                      v-model="formData.startDate"
                      outlined
                      :label="$gettext('START_DATE') + ' *'"
                      mask="##/##/####"
                      :disable="isRegularOrInstallmentInvoicePurpose"
                      :rules="[val => !!val || $gettext('REQUIRED_FIELD')]"
                    >
                      <template v-slot:append>
                        <q-icon name="event" class="cursor-pointer">
                          <q-popup-proxy>
                            <q-date v-model="formData.startDate" mask="DD/MM/YYYY">
                              <div class="row items-center justify-end">
                                <q-btn v-close-popup :label="$gettext('CLOSE')" color="primary" flat />
                              </div>
                            </q-date>
                          </q-popup-proxy>
                        </q-icon>
                      </template>
                    </q-input>
                  </div>
                  <div class="col-12 col-md-6">
                    <q-input
                      ref="inputCampaignEndDate"
                      v-model="formData.endDate"
                      outlined
                      :label="endDateLabel"
                      mask="##/##/####"
                      :rules="isRecurring ? [val => !!val || $gettext('REQUIRED_FIELD')] : []"
                    >
                      <template v-slot:append>
                        <q-icon name="event" class="cursor-pointer">
                          <q-popup-proxy>
                            <q-date v-model="formData.endDate" mask="DD/MM/YYYY">
                              <div class="row items-center justify-end">
                                <q-btn v-close-popup :label="$gettext('CLOSE')" color="primary" flat />
                              </div>
                            </q-date>
                          </q-popup-proxy>
                        </q-icon>
                      </template>
                    </q-input>
                  </div>
                  <div class="col-12 col-md-12">
                    <recurrence-calendar
                      :campaign-id="currentId"
                      :start-date="formData.startDate"
                      :recurrence-type="formData.recurrenceType"
                      :custom-interval="formData.customInterval"
                      :lead-time-days="isAdditionalPurpose ? null : formData.leadTimeDays"
                      :messaging-purpose="selectedTemplatePurpose"
                      :end-date="formData.endDate"
                    />
                  </div>
                </div>
              </q-card-section>
            </q-card>
            <!-- Regras de Negócio -->
            <!--
            <q-card flat class="q-mb">
              <q-card-section class="q-py-sm q-card__section q-card__section--vert">
                <div class="crud-title">
                  <span>{{ $gettext('CAMPAIGN_BUSINESS_RULE') }}</span>
                </div>
              </q-card-section>
              <q-card-section class="section-body">
                <div class="business-rules-list">
                  <div class="business-rule-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00613b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rule-icon" aria-hidden="true">
                      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                      <path d="m9 12 2 2 4-4"></path>
                    </svg>
                    <p class="rule-text">{{ $gettext('CAMPAIGN_BUSINESS_RULE_ANTI_DUPLICATE') }}</p>
                  </div>
                  <div class="business-rule-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0e88e4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rule-icon" aria-hidden="true">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <path d="M16 3.128a4 4 0 0 1 0 7.744"></path>
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                    </svg>
                    <p class="rule-text">{{ $gettext('CAMPAIGN_BUSINESS_RULE_OPT_IN_OPT_OUT') }}</p>
                  </div>
                  <div class="business-rule-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d18e4b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rule-icon" aria-hidden="true">
                      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
                      <path d="m9 12 2 2 4-4"></path>
                    </svg>
                    <p class="rule-text">{{ $gettext('CAMPAIGN_BUSINESS_RULE_LGPD') }}</p>
                  </div>
                  <div class="business-rule-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0062a9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rule-icon" aria-hidden="true">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 6v6l4 2"></path>
                    </svg>
                    <p class="rule-text">{{ $gettext('CAMPAIGN_BUSINESS_RULE_IMMUTABILITY') }}</p>
                  </div>
                </div>
              </q-card-section>
            </q-card>
            -->
          </div>

          <!-- RIGHT -->
          <div class="campaign-col-right col-12 col-md-6">
            <campaign-preview-panel
              :subject="previewSubject"
              :body-html="previewBodyHtml"
              :purpose-label="selectedTemplatePurposeLabel"
              :channel-type="selectedTemplateChannelType"
              :header-image="previewHeaderImage"
              :footer-image="previewFooterImage"
              :message-variables="availableMessageVariables"
              :start-date="formData.startDate"
              :end-date="formData.endDate"
              :lead-time-days="isAdditionalPurpose ? null : formData.leadTimeDays"
            />
          </div>

        </div>
      </q-form>

      <!-- FOOTER -->
      <div class="campaign-modal__footer">
        <div class="row justify-center q-gutter-x-md">
          <q-btn :label="$gettext('CANCEL')" color="primary" @click="onCancel" class="q-px-xl" />
          <q-btn :label="$gettext('SAVE')" color="green" @click="onSave" class="q-px-xl" />
        </div>
      </div>

    </div>
  </q-dialog>
</template>

<script>
import { Component, Vue, Watch } from 'vue-property-decorator'
import { messageBox, repository } from 'jtech'
import CampaignPreviewPanel from './CampaignPreviewPanel.vue'
import { recurrenceType } from 'src/shared/inf-tabela-descricao/RecurrenceType.enum.js'
import { variableType } from 'src/shared/inf-tabela-descricao/VariableType.enum.js'
import { messagingPurpose } from 'src/shared/inf-tabela-descricao/MessagingPurpose.enum'
import { templateImageType } from 'src/shared/inf-tabela-descricao/TemplateImageType.enum.js'
import RecurrenceCalendar from './RecurrenceCalendar.vue'

const CAMPAIGN_LOAD_INTERFACE = 'campaign.pesquisar'
const CAMPAIGN_SAVE_INTERFACE = 'campaign.gravar'
const CAMPAIGN_EDIT_INTERFACE = 'campaign.alterar'
const TEMPLATE_DETAIL_INTERFACE = 'template.detalhar'
const CONCESSION_CONTRACT_TARIFF_DATATABLE_INTERFACE = 'concessionContractTariff.hql'
const NEIGHBORHOOD_DATATABLE_INTERFACE = 'neighborhood.pesquisar'
const MSG_VARIABLE_ENUM_INTERFACE = 'msgVariableEnum.obterTodos'

@Component({
  name: 'CampaignEditModal',
  components: {
    CampaignPreviewPanel,
    RecurrenceCalendar
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  }
})
class CampaignEditModal extends Vue {
  showModal = false
  mode = 'create'
  currentId = null

  formData = {
    name: '',
    template: null,
    status: null,
    recurrenceType: null,
    // toggles se a campanha é recorrente (usado pelo checkbox editável)
    isRecurringToggle: false,
    customInterval: null,
    leadTimeDays: null,
    startDate: '',
    endDate: '',
    preferredTime: '',
    concessionContractTariff: null,
    neighborhood: null
  }

  get isRecurring () {
    return !!this.recurringToggle
  }

  get isCustomRecurrence () {
    const rt = recurrenceType()
    return !!(this.formData.recurrenceType && String(this.formData.recurrenceType) === String(rt.CUSTOM.key))
  }

  get isDailyRecurrence () {
    const rt = recurrenceType()
    return !!(this.formData.recurrenceType && String(this.formData.recurrenceType) === String(rt.DAILY.key))
  }

  get endDateLabel () {
    if (!this.isRecurring) {
      return this.$gettext('END_DATE')
    }
    return this.$gettext('END_DATE') + ' *'
  }

  // controle local para permitir alternar recorrência no formulário
  recurringToggle = false

  originalFormData = {}

  templateOptions = []
  statusOptions = []
  recurrenceTypeOptions = []
  concessionContractTariffOptions = []
  neighborhoodOptions = []
  messageVariables = []

  previewSubject = ''
  previewBodyHtml = ''
  selectedTemplatePurposeLabel = ''
  selectedTemplatePurpose = null
  // channel-related: only store the channelType from template, compute label/color/icon via methods
  selectedTemplateChannelType = null
  previewHeaderImage = null
  previewFooterImage = null

  concessionContractTariffReadOnly = false
  concessionContractTariffRaw = null

  mounted () {
    this.showModal = this.show
  }

  get selectedTemplateName () {
    const selected = this.templateOptions.find(t => t.value === this.formData.template)
    return selected ? selected.label : ''
  }

  get hasTemplateSelected () {
    return this.formData.template !== null && this.formData.template !== undefined && this.formData.template !== ''
  }

  get availableMessageVariables () {
    return this.hasTemplateSelected ? this.messageVariables : []
  }

  async openCreate () {
    this.mode = 'create'
    this.currentId = null
    this.resetForm()
    await this.loadCombos()
    // Define status padrão como rascunho
    if (this.statusOptions.length > 0) {
      this.formData.status = this.statusOptions[0].value
    }
    this.showModal = true
    this.saveOriginalFormData()
    try {
      const rt = recurrenceType()
      this.recurringToggle = !!(this.formData.recurrenceType && String(this.formData.recurrenceType) !== String(rt.ONE_TIME.key))
    } catch (e) {}
    this.$emit('update:show', true)
  }

  async openEdit (id) {
    // Resetar apenas os dados do formulário, não mode e currentId
    this.formData = {
      name: '',
      template: null,
      status: null,
      recurrenceType: null,
      customInterval: null,
      leadTimeDays: null,
      startDate: '',
      endDate: '',
      preferredTime: '',
      concessionContractTariff: null,
      neighborhood: null
    }
    this.clearPreview()
    this.concessionContractTariffReadOnly = false
    this.concessionContractTariffRaw = null

    this.mode = 'edit'
    this.currentId = id
    await this.loadCombos()
    await this.loadRecord(id)
    this.showModal = true
    this.saveOriginalFormData()
    try {
      const rt = recurrenceType()
      this.recurringToggle = !!(this.formData.recurrenceType && String(this.formData.recurrenceType) !== String(rt.ONE_TIME.key))
    } catch (e) {}
    this.$emit('update:show', true)
  }

  async loadRecord (id) {
    try {
      const response = await repository.resolve(CAMPAIGN_EDIT_INTERFACE, { id })
      const form = response?.dadosForm || response?.[0]?.dadosForm || response?.[0] || {}
      await this.applyFormData(form)

      // After applying form data, try to load neighborhoods if we have a city from the concession tariff data
      const raw = this.concessionContractTariffRaw
      let cityId = raw?.city || raw?.cidade || raw?.cityId || null
      if (!cityId && this.formData.concessionContractTariff) {
        const selected = this.concessionContractTariffOptions.find(opt => opt.value === this.formData.concessionContractTariff)
        cityId = selected?.raw?.city || selected?.raw?.cidade || selected?.raw?.cityId || null
      }
      if (cityId) {
        await this.loadNeighborhoods(cityId)
      }
    } catch (error) {
      messageBox.error(this.$gettext('ERROR_LOADING_FORM'), 'top-right')
    }
  }

  normalizeDate (dateValue) {
    if (!dateValue) return ''

    // Se já estiver em formato DD/MM/YYYY, não faz nada
    if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      return dateValue
    }

    // Se for string ISO (YYYY-MM-DD ou YYYY-MM-DDTHH:mm:ss), converte para DD/MM/YYYY
    if (typeof dateValue === 'string' && dateValue.includes('-')) {
      try {
        const date = new Date(dateValue)
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, '0')
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const year = date.getFullYear()
          return `${day}/${month}/${year}`
        }
      } catch (e) {
        console.warn('Erro ao parsear data ISO:', dateValue)
      }
    }

    // Se for timestamp, converte para DD/MM/YYYY
    if (typeof dateValue === 'number') {
      try {
        const date = new Date(dateValue)
        if (!isNaN(date.getTime())) {
          const day = String(date.getDate()).padStart(2, '0')
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const year = date.getFullYear()
          return `${day}/${month}/${year}`
        }
      } catch (e) {
        console.warn('Erro ao parsear timestamp:', dateValue)
      }
    }

    // Se for Data object
    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      const day = String(dateValue.getDate()).padStart(2, '0')
      const month = String(dateValue.getMonth() + 1).padStart(2, '0')
      const year = dateValue.getFullYear()
      return `${day}/${month}/${year}`
    }

    return String(dateValue)
  }

  async applyFormData (form) {
    this.formData.name = form.name || ''
    this.formData.template = this.getIdValue(form.template)
    this.formData.status = this.getIdValue(form.status)
    this.formData.recurrenceType = this.getIdValue(form.recurrenceType)
    // `recurrenceType` agora determina se é recorrente; ONE_TIME não é recorrente.
    // sincroniza toggle editável
    try {
      const rt = recurrenceType()
      this.recurringToggle = !!(this.formData.recurrenceType && String(this.formData.recurrenceType) !== String(rt.ONE_TIME.key))
    } catch (e) {
      this.recurringToggle = !!this.formData.recurrenceType
    }

    this.formData.leadTimeDays = form.leadTimeDays || null
    // Load customInterval if provided by backend
    this.formData.customInterval = form.customInterval || form.recurrenceCustomInterval || null
    this.formData.startDate = this.normalizeDate(form.startDate)
    this.formData.endDate = this.normalizeDate(form.endDate)
    this.formData.preferredTime = form.preferredTime || ''

    // concessionContractTariff data may contain concessionContractTariff and neighborhood
    this.formData.concessionContractTariff = null
    this.formData.neighborhood = null
    this.concessionContractTariffRaw = null
    if (form.segmentationFilter) {
      let parsed = null
      if (typeof form.segmentationFilter === 'string') {
        try {
          parsed = JSON.parse(form.segmentationFilter)
        } catch (e) {
          parsed = null
        }
      } else if (typeof form.segmentationFilter === 'object') {
        parsed = form.segmentationFilter
      }

      if (parsed) {
        // parsed can be either:
        // 1) { concessionContractTariff: 7, label: '...' }
        // 2) { concessionContractTariff: { id:7, label: '...' }, neighborhood: { id:1, label:'Centro' } }

        // Normalize concessionContractTariff
        const rawCct = parsed.concessionContractTariff
        if (rawCct && typeof rawCct === 'object') {
          this.concessionContractTariffRaw = rawCct
          this.formData.concessionContractTariff = rawCct.id || rawCct.value || null
        } else {
          // raw may be a primitive id or parsed has label at top-level
          this.concessionContractTariffRaw = parsed
          this.formData.concessionContractTariff = parsed.concessionContractTariff || parsed.id || parsed.concessionContractTariffId || null
        }

        // Normalize neighborhood
        const rawNb = parsed.neighborhood
        if (rawNb && typeof rawNb === 'object') {
          this.formData.neighborhood = rawNb.id || rawNb.value || null
        } else {
          this.formData.neighborhood = parsed.neighborhood || parsed.neighborhoodId || parsed.bairro || null
        }
      } else {
        // fallback para valores simples
        const simple = this.parseConcessionContractTariff(form.segmentationFilter)
        this.formData.concessionContractTariff = simple
      }
    }

    if (this.formData.template) {
      await this.loadTemplatePreview(this.formData.template)
      // Após carregar o template, recarregar os períodos tarifários conforme a lógica da grid
      await this.loadConcessionContractTariffs()
    }

    // Se em modo edição, verificar disponibilidade do concessionContractTariff
    // (deve ser chamado após loadConcessionContractTariffs para checar corretamente se a opção existe)
    if (this.mode === 'edit' && this.formData.concessionContractTariff) {
      this.checkAndUpdateConcessionContractTariffAvailability()
    }
  }

  getIdValue (value) {
    if (value && typeof value === 'object') {
      return value.id || value.chCodigo || value.value || null
    }
    return value || null
  }

  parseConcessionContractTariff (value) {
    if (!value) return null

    // Expect JSON string like {"concessionContractTariff":13,"label":"..."}
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value)
        return parsed.concessionContractTariff || parsed.id || null
      } catch (e) {
        return null
      }
    }

    // Se for objeto, pode ser o objeto inteiro ou o wrapper { concessionContractTariff: { id,.. } }
    if (typeof value === 'object') {
      // If wrapped: { concessionContractTariff: { id: ... } }
      if (value.concessionContractTariff) {
        const nested = value.concessionContractTariff
        if (typeof nested === 'object') return nested.id || nested.value || null
        return nested || null
      }
      // If direct object: { id: 7 }
      if (value.id) return value.id
      return null
    }
    return value || null
  }

  checkAndUpdateConcessionContractTariffAvailability () {
    const currentValue = this.formData.concessionContractTariff
    const optionExists = this.concessionContractTariffOptions.some(opt => opt.value === currentValue)

    if (!optionExists && this.concessionContractTariffRaw) {
      // Adicionar a opção original à lista (desabilitada, no início)
      this.concessionContractTariffOptions.unshift({
        label: this.concessionContractTariffRaw.label || `Contract ${currentValue}`,
        value: currentValue,
        raw: this.concessionContractTariffRaw,
        disabled: true
      })
      this.concessionContractTariffReadOnly = false
    } else {
      this.concessionContractTariffReadOnly = false
    }
  }

  buildSegmentationFilter (concessionContractTariffId, neighborhoodId) {
    if (!concessionContractTariffId && !neighborhoodId) return null

    // Se o campo está desabilitado, usar os dados originais (JSON esperado)
    if (this.concessionContractTariffReadOnly && this.concessionContractTariffRaw) {
      const raw = this.concessionContractTariffRaw
      const cctId = raw.concessionContractTariff || raw.id || raw.concessionContractTariffId || raw.value || null
      const cctLabel = raw.label || raw.description || raw.name || ''
      const rawNb = raw.neighborhood || raw.neighborhoodId || raw.bairro || null

      const payload = {
        concessionContractTariff: { id: cctId, label: cctLabel }
      }

      if (rawNb) {
        if (typeof rawNb === 'object') {
          payload.neighborhood = { id: rawNb.id || rawNb.value || null, label: rawNb.label || rawNb.name || rawNb.description || '' }
        } else {
          payload.neighborhood = { id: rawNb, label: '' }
        }
      }

      return JSON.stringify(payload)
    }

    // Encontrar o periodo tarifario selecionado nas opções
    const selected = this.concessionContractTariffOptions.find(opt => opt.value === concessionContractTariffId)
    const neigh = this.neighborhoodOptions.find(opt => opt.value === neighborhoodId)

    const payload = {}
    if (concessionContractTariffId) {
      payload.concessionContractTariff = {
        id: concessionContractTariffId,
        label: selected ? selected.label : (this.concessionContractTariffRaw?.label || '')
      }
    }
    if (neighborhoodId) {
      payload.neighborhood = {
        id: neighborhoodId,
        label: neigh ? neigh.label : ''
      }
    }

    return JSON.stringify(payload)
  }

  convertToBackendFormat (dateStr) {
    if (!dateStr) return ''

    // Se estiver em formato DD/MM/YYYY, converte para YYYY-MM-DD
    if (typeof dateStr === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('/')
      return `${year}-${month}-${day}`
    }

    return dateStr
  }

  async loadCombos () {
    try {
      const [form, variablesForm] = await Promise.all([
        repository.resolve(CAMPAIGN_LOAD_INTERFACE),
        repository.resolveLogic(MSG_VARIABLE_ENUM_INTERFACE)
      ])
      const rel = this.getRelacionamentos(form)

      this.templateOptions = this.normalizeOptions(rel, ['template'])
      this.statusOptions = this.normalizeOptions(rel, ['campaignStatus'])
      this.recurrenceTypeOptions = this.normalizeOptions(rel, ['recurrenceType'])
      this.messageVariables = this.normalizeVariables(variablesForm)

      if (this.templateOptions.length === 0 || this.statusOptions.length === 0) {
        const saveForm = await repository.resolve(CAMPAIGN_SAVE_INTERFACE)
        const saveRel = this.getRelacionamentos(saveForm)
        if (this.templateOptions.length === 0) {
          this.templateOptions = this.normalizeOptions(saveRel, ['template'])
        }
        if (this.statusOptions.length === 0) {
          this.statusOptions = this.normalizeOptions(saveRel, ['campaignStatus'])
        }
        if (this.recurrenceTypeOptions.length === 0) {
          this.recurrenceTypeOptions = this.normalizeOptions(saveRel, ['recurrenceType'])
        }
      }
    } catch (error) {
      console.error('Erro ao carregar combos:', error)
      this.templateOptions = []
      this.statusOptions = []
      this.recurrenceTypeOptions = []
      this.concessionContractTariffOptions = []
      this.messageVariables = []
    }
  }

  normalizeVariables (data) {
    const records = Array.isArray(data)
      ? data
      : (Array.isArray(data?.registros) ? data.registros : (Array.isArray(data?.[0]?.registros) ? data[0].registros : []))

    const result = records
      .map(category => ({
        variableType: category.variableType,
        nmDescricao: category.nmDescricao,
        variables: Array.isArray(category.variables)
          ? category.variables.map(variable => ({
            technicalKey: variable.technicalKey,
            displayLabel: variable.displayLabel,
            displayTag: variable.displayTag,
            variableType: variable.variableType,
            sampleText: variable.sampleText
          }))
          : []
      }))
      .filter(category => category.variables.length > 0)

    const varType = variableType()
    const getCategoryOrder = (typeId) => {
      if (typeId === varType.FINANCE.key) return 1
      if (typeId === varType.CONCESSION_CONTRACT_TARIFF.key) return 2
      if (typeId === varType.CONSUMER_UNIT_DATA.key) return 3
      if (typeId === varType.ORGANIZATION.key) return 4
      if (typeId === varType.CONTROL.key) return 5
      return 99
    }

    result.sort((a, b) => getCategoryOrder(a.variableType) - getCategoryOrder(b.variableType))
    return result
  }

  async loadConcessionContractTariffs () {
    try {
      let gridName = ''
      if (this.selectedTemplatePurpose == messagingPurpose().ONE_TIME_PAYMENT.key) {
        gridName = 'obterElegiveisOneTimePaymentEmail'
      } else if (this.selectedTemplatePurpose == messagingPurpose().ADDITIONAL.key) {
        gridName = 'obterElegiveisAdditionalEmail'
      } else if (this.selectedTemplatePurpose == messagingPurpose().REGULAR_INVOICE.key) {
        gridName = 'obterElegiveisRegularInvoiceEmail'
      } else if (this.selectedTemplatePurpose == messagingPurpose().INSTALLMENT.key) {
        gridName = 'obterElegiveisInstallmentEmail'
      } else {
        this.concessionContractTariffOptions = []
        return
      }
      const response = await repository.resolveDatatableModel(
        CONCESSION_CONTRACT_TARIFF_DATATABLE_INTERFACE,
        {
          grid: gridName
        }
      )

      const contracts = response?.registros || []

      this.concessionContractTariffOptions = contracts.map(item => ({
        label: item.description,
        value: item.id,
        raw: item
      }))

      // Em REGULAR_INVOICE, se já houver período tarifário selecionado, sincroniza recorrência/data.
      if (this.formData.concessionContractTariff && this.isRegularOrInstallmentInvoicePurpose) {
        const selected = this.concessionContractTariffOptions.find(opt => opt.value === this.formData.concessionContractTariff)
        this.applyRegularInvoiceDefaultsFromTariff(selected)
      }
    } catch (error) {
      console.warn('Erro ao carregar períodos tarifários:', error)
      this.concessionContractTariffOptions = []
    }
  }

  get isRegularOrInstallmentInvoicePurpose () {
    return String(this.selectedTemplatePurpose) === String(messagingPurpose().REGULAR_INVOICE.key) ||
           String(this.selectedTemplatePurpose) === String(messagingPurpose().INSTALLMENT.key) ||
           String(this.selectedTemplatePurpose) === String(messagingPurpose().ADDITIONAL.key)
  }

  get isAdditionalPurpose () {
    return String(this.selectedTemplatePurpose) === String(messagingPurpose().ADDITIONAL.key)
  }

  applyRegularInvoiceDefaultsFromTariff (selectedOption) {
    if (this.mode !== 'create' || !this.isRegularOrInstallmentInvoicePurpose || !selectedOption?.raw) return

    const rt = recurrenceType()
    this.recurringToggle = true
    const tariffRecurrenceType = selectedOption.raw.recurrenceType || selectedOption.raw.recurrenceTypeId || null
    this.formData.recurrenceType = tariffRecurrenceType || rt.MONTHLY.key

    const tariffStartDate = selectedOption.raw.startDate || selectedOption.raw.dtInicio || null
    const tariffEndDate = selectedOption.raw.endDate || selectedOption.raw.dtFim || null

    if (tariffStartDate) {
      this.formData.startDate = this.normalizeDate(tariffStartDate)
    }
    if (tariffEndDate) {
      this.formData.endDate = this.normalizeDate(tariffEndDate)
    }
  }

  @Watch('formData.concessionContractTariff')
  async onConcessionContractTariffChange (newVal) {
    if (!newVal) {
      this.neighborhoodOptions = []
      this.formData.neighborhood = null
      return
    }

    const selected = this.concessionContractTariffOptions.find(opt => opt.value === newVal)
    const cityId = selected?.raw?.city || selected?.raw?.cidade || selected?.raw?.cityId || null

    this.applyRegularInvoiceDefaultsFromTariff(selected)

    if (cityId) {
      await this.loadNeighborhoods(cityId)
    } else {
      this.neighborhoodOptions = []
    }

    this.formData.neighborhood = null
  }

  async loadNeighborhoods (cityId) {
    try {
      if (!cityId) {
        this.neighborhoodOptions = []
        return
      }

      const param = { city: cityId }
      const { registros } = await repository.resolveDatatableModel(
        NEIGHBORHOOD_DATATABLE_INTERFACE,
        param
      )

      const records = Array.isArray(registros) ? registros : []

      this.neighborhoodOptions = records.map(item => ({
        label: item.name || item.description || item.label || String(item.id || ''),
        value: item.id || item.value || item.codigo || item,
        raw: item
      }))
    } catch (error) {
      console.warn('Erro ao carregar bairros:', error)
      this.neighborhoodOptions = []
    }
  }

  getRelacionamentos (data) {
    if (!data) return {}
    if (Array.isArray(data)) return data[0]?.relacionamentos || data[0] || {}
    return data.relacionamentos || data
  }

  normalizeOptions (data, preferredKeys = []) {
    let records = []
    if (!data) return []

    if (data.reRender) {
      const key = preferredKeys.find(k => data.reRender[k])
      if (key) records = Array.isArray(data.reRender[key]) ? data.reRender[key] : []
    } else if (preferredKeys.length > 0) {
      const key = preferredKeys.find(k => data[k])
      if (key) records = Array.isArray(data[key]) ? data[key] : []
    }

    return records
      .map(item => ({
        label: item.nmDescricao || item.label || item.name || String(item.id || ''),
        value: item.id || item.value || item.codigo || item,
        raw: item
      }))
      .filter(item => item.value !== undefined && item.value !== null)
  }

  async onTemplateChange (templateId) {
    if (!templateId) {
      this.clearPreview()
      return
    }
    await this.loadTemplatePreview(templateId)
    if (this.mode === 'create' && this.isRegularOrInstallmentInvoicePurpose) {
      const rt = recurrenceType()
      this.recurringToggle = true
      this.formData.recurrenceType = rt.MONTHLY.key
      this.formData.customInterval = null
    }
    // Ao mudar o template, recarregar os períodos tarifários conforme a lógica da grid
    await this.loadConcessionContractTariffs()
    // limpar seleção de período tarifário e bairro ao trocar template
    this.formData.concessionContractTariff = null
    this.neighborhoodOptions = []
  }

  async loadTemplatePreview (templateId) {
    try {
      const response = await repository.resolve(TEMPLATE_DETAIL_INTERFACE, { id: templateId })
      const form = response?.dadosForm || response?.[0]?.dadosForm || response?.[0] || {}

      this.previewSubject = form.subject || ''
      this.previewBodyHtml = form.content || ''
      this.selectedTemplatePurposeLabel = form.messagingPurpose_nmDescricao || ''
      this.selectedTemplatePurpose = form.messagingPurpose || null
      if (String(this.selectedTemplatePurpose) === String(messagingPurpose().ADDITIONAL.key)) {
        this.formData.leadTimeDays = null
      }
      this.previewHeaderImage = null
      this.previewFooterImage = null

      // Extrair apenas o channelType do template; label/color/icon são calculados pelos getters
      const channelTypeValue = form.channelType || form.channel_type || form.channelId || null
      this.selectedTemplateChannelType = channelTypeValue

      // Carregar imagens do template (padrão Detail)
      const images = form.msgTemplateImages || form.templateImages || []
      const tplType = templateImageType()
      if (Array.isArray(images) && images.length > 0) {
        for (const img of images) {
          const fileName = img.nmArquivo || img.fileName || img.name
          const type = img.templateImageType || img.imageType || img.type
          if (!fileName) continue
          try {
            const blob = await repository.downloadFile(
              fileName,
              null,
              'MESSAGING_TEMPLATE_IMAGES'
            )
            const url = URL.createObjectURL(blob)
            const tKey = (type !== null && type !== undefined) ? Number(type) : null
            if (tKey === tplType.HEADER.key) {
              this.previewHeaderImage = url
            } else if (tKey === tplType.FOOTER.key) {
              this.previewFooterImage = url
            }
          } catch (e) {
            console.warn('Erro ao baixar imagem do template:', fileName, e)
          }
        }
      }
    } catch (error) {
      console.warn('Erro ao carregar preview do template:', error)
      this.clearPreview()
    }
  }

  clearPreview () {
    this.previewSubject = ''
    this.previewBodyHtml = ''
    this.previewHeaderImage = null
    this.previewFooterImage = null
    this.selectedTemplatePurposeLabel = ''
    this.selectedTemplatePurpose = null
  }

  saveOriginalFormData () {
    this.originalFormData = { ...this.formData }
  }

  getChangedFields () {
    const changes = []
    const fieldLabels = {
      name: this.$gettext('CAMPAIGN_NAME'),
      template: this.$gettext('TEMPLATE'),
      status: this.$gettext('STATUS'),
      recurrenceType: this.$gettext('RECURRENCE_TYPE'),
      customInterval: this.$gettext('CUSTOM_INTERVAL'),
      leadTimeDays: this.$gettext('LEAD_TIME_DAYS'),
      startDate: this.$gettext('START_DATE'),
      endDate: this.$gettext('END_DATE'),
      preferredTime: this.$gettext('PREFERRED_TIME'),
      concessionContractTariff: this.$gettext('CONCESSION_CONTRACT_TARIFF'),
      neighborhood: this.$gettext('NEIGHBORHOOD')
    }
    for (const [key, label] of Object.entries(fieldLabels)) {
      if (this.formData[key] !== this.originalFormData[key]) {
        changes.push(label)
      }
    }
    return changes
  }

  async onCloseButtonClick () {
    await this.onCancel()
  }

  async onCancel () {
    const changedFields = this.getChangedFields()
    if (changedFields.length > 0) {
      const confirmed = await this.showDiscardChangesDialog(changedFields)
      if (!confirmed) return
    }
    this.showModal = false
    this.resetForm()
  }

  async onSave () {
    const form = this.$refs.campaignForm
    if (form && typeof form.validate === 'function') {
      await form.validate()
    }

    this.$refs.inputCampaignName?.validate()
    this.$refs.inputCampaignTemplate?.validate()
    this.$refs.inputCampaignConcessionTariff?.validate()
    this.$refs.inputCampaignStatus?.validate()
    this.$refs.inputCampaignStartDate?.validate()
    this.$refs.inputCampaignEndDate?.validate()

    if (!this.formData.name) {
      messageBox.warn(this.$gettext('CAMPAIGN_NAME_REQUIRED'), 'top-right')
      return
    }
    if (!this.formData.template) {
      messageBox.warn(this.$gettext('CAMPAIGN_TEMPLATE_REQUIRED'), 'top-right')
      return
    }
    if (!this.formData.concessionContractTariff) {
      messageBox.warn(this.$gettext('REQUIRED_FIELD'), 'top-right')
      return
    }
    if (!this.formData.status) {
      messageBox.warn(this.$gettext('CAMPAIGN_STATUS_REQUIRED'), 'top-right')
      return
    }
    if (!this.formData.startDate) {
      messageBox.warn(this.$gettext('CAMPAIGN_START_DATE_REQUIRED'), 'top-right')
      return
    }
    // endDate só é obrigatório se for recorrente
    if (this.recurringToggle && !this.formData.endDate) {
      messageBox.warn(this.$gettext('CAMPAIGN_END_DATE_REQUIRED'), 'top-right')
      return
    }

    // Validação: recorrência custom exige intervalo
    if (this.recurringToggle && String(this.formData.recurrenceType) === String(recurrenceType().CUSTOM.key)) {
      const intervalDays = Number(this.formData.customInterval) || null
      if (intervalDays === null) {
        messageBox.warn(this.$gettext('CUSTOM_RECURRENCE_REQUIRES_INTERVAL'), 'top-right')
        return
      }
    }

    const changedFields = this.getChangedFields()
    if (changedFields.length === 0) {
      messageBox.info(this.$gettext('MSG_TEMPLATE_NO_CHANGES'), 'top-right')
      return
    }

    const confirmed = await this.showConfirmSaveDialog(changedFields)
    if (!confirmed) return

    try {
      const payload = {
        name: this.formData.name,
        template: this.formData.template,
        status: this.formData.status,
        leadTimeDays: this.isAdditionalPurpose ? null : this.formData.leadTimeDays,
        startDate: this.convertToBackendFormat(this.formData.startDate),
        endDate: this.convertToBackendFormat(this.formData.endDate),
        preferredTime: this.formData.preferredTime,
        segmentationFilter: this.buildSegmentationFilter(this.formData.concessionContractTariff, this.formData.neighborhood),
        recurrenceType: this.recurringToggle ? (this.formData.recurrenceType || recurrenceType().ONE_TIME.key) : recurrenceType().ONE_TIME.key,
        customInterval: this.isCustomRecurrence && this.recurringToggle ? this.formData.customInterval : null
      }

      if (this.mode === 'edit' && this.currentId) {
        await repository.update(CAMPAIGN_EDIT_INTERFACE, { id: this.currentId, ...payload })
      } else {
        await repository.create(CAMPAIGN_SAVE_INTERFACE, payload)
      }

      this.$emit('campaign-saved')
      this.showModal = false
      this.resetForm()
    } catch (error) {
      console.error('Erro ao salvar campanha:', error)
    }
  }

  resetForm () {
    this.formData = {
      name: '',
      template: null,
      status: null,
      recurrenceType: null,
      leadTimeDays: null,
      customInterval: null,
      startDate: '',
      endDate: '',
      preferredTime: '',
      concessionContractTariff: null,
      neighborhood: null
    }
    this.mode = 'create'
    this.currentId = null
    this.concessionContractTariffReadOnly = false
    this.concessionContractTariffRaw = null
    this.clearPreview()
  }

  showConfirmSaveDialog (changedFields) {
    return new Promise((resolve) => {
      const itemsList = changedFields.map(field => `
        <div style="display: flex; align-items: flex-start; gap: 8px;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d18e4b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-top: 2px; flex-shrink: 0;">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
            <path d="M12 9v4"></path><path d="M12 17h.01"></path>
          </svg>
          <span style="font-size: 12px; color: #52525b;">${this.$gettext('MSG_TEMPLATE_FIELD_CHANGED')}: ${field}</span>
        </div>`).join('')

      const campaignName = this.formData.name || 'Campanha'
      this.$q.dialog({
        title: this.$gettext('MSG_TEMPLATE_SAVE_TITLE'),
        message: `<div style="text-align:left;"><div style="margin-bottom:16px;line-height:1.5;color:#333;">${this.$gettext('MSG_TEMPLATE_CONFIRM_UPDATE')} "${campaignName}"?</div><div style="background:#f8fafc;border:1px solid #e8ecf0;border-radius:6px;padding:12px;">${itemsList}</div></div>`,
        html: true,
        ok: { push: true, label: this.$gettext('SAVE'), color: 'primary' },
        cancel: { push: true, label: this.$gettext('CANCEL'), color: 'grey-4', textColor: 'grey-9' }
      }).onOk(() => resolve(true)).onCancel(() => resolve(false))
    })
  }

  showDiscardChangesDialog (changedFields) {
    return new Promise((resolve) => {
      const itemsList = changedFields.map(field => `
        <div style="display: flex; align-items: flex-start; gap: 8px;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d18e4b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-top: 2px; flex-shrink: 0;">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
            <path d="M12 9v4"></path><path d="M12 17h.01"></path>
          </svg>
          <span style="font-size: 12px; color: #52525b;">${this.$gettext('MSG_TEMPLATE_WILL_DISCARD')}: ${field}</span>
        </div>`).join('')

      this.$q.dialog({
        title: this.$gettext('MSG_TEMPLATE_DISCARD_TITLE'),
        message: `<div style="text-align:left;"><div style="margin-bottom:16px;line-height:1.5;color:#333;">${this.$gettext('MSG_TEMPLATE_DISCARD_UNSAVED_MSG')}</div><div style="background:#f8fafc;border:1px solid #e8ecf0;border-radius:6px;padding:12px;">${itemsList}</div></div>`,
        html: true,
        ok: { push: true, label: this.$gettext('MSG_TEMPLATE_DISCARD_BTN'), color: 'primary' },
        cancel: { push: true, label: this.$gettext('MSG_TEMPLATE_KEEP_EDITING_BTN'), color: 'grey-4', textColor: 'grey-9' }
      }).onOk(() => resolve(true)).onCancel(() => resolve(false))
    })
  }

  onHide () {
    this.$emit('update:show', false)
    this.resetForm()
  }

  @Watch('show')
  onShowChange (newVal) {
    this.showModal = newVal
  }

  @Watch('recurringToggle')
  onRecurringToggleChange (newVal) {
    try {
      const rt = recurrenceType()
      if (this.mode === 'create' && this.isRegularOrInstallmentInvoicePurpose) {
        this.recurringToggle = true
        this.formData.recurrenceType = rt.MONTHLY.key
        this.formData.customInterval = null
        return
      }
      if (!newVal) {
        // desmarcou recorrência -> forçar ONE_TIME
        this.formData.recurrenceType = rt.ONE_TIME.key
      } else {
        // marcou recorrência -> se estava ONE_TIME limpar para permitir seleção
        if (String(this.formData.recurrenceType) === String(rt.ONE_TIME.key)) {
          this.formData.recurrenceType = null
        }
      }
    } catch (e) {
      if (!newVal) this.formData.recurrenceType = null
    }
  }

  clampLeadTimeDays (value) {
    if (value === null || value === undefined || value === '') return null

    const numericValue = Number(value)
    if (isNaN(numericValue)) return null

    return Math.min(15, Math.max(0, numericValue))
  }

  onLeadTimeDaysInput (value) {
    const clampedValue = this.clampLeadTimeDays(value)
    if (clampedValue !== this.formData.leadTimeDays) {
      this.formData.leadTimeDays = clampedValue
    }
  }

  @Watch('formData.leadTimeDays')
  onLeadTimeDaysChange (newVal) {
    const clampedValue = this.clampLeadTimeDays(newVal)
    if (clampedValue === null && newVal !== null && newVal !== undefined && newVal !== '') {
      this.formData.leadTimeDays = null
      return
    }

    if (clampedValue !== newVal) {
      this.formData.leadTimeDays = clampedValue
    }
  }

  @Watch('showModal')
  onShowModalChange (newVal) {
    if (!newVal) {
      this.$emit('update:show', false)
    }
  }
}

export default CampaignEditModal
</script>

<style lang="stylus" scoped>
.campaign-modal
  display: flex
  flex-direction: column
  width: 85vw
  max-width: 85vw
  height: 90vh
  max-height: 90vh
  background: white
  border-radius: 4px
  overflow: hidden

.campaign-modal__header
  flex-shrink: 0

.campaign-modal__body
  flex: 1
  min-height: 0
  overflow: hidden
  padding: 16px 16px 0 16px

.campaign-modal__footer
  flex-shrink: 0
  background: #ffffff
  border-top: 1px solid rgba(0, 0, 0, 0.12)
  padding: 16px

.campaign-container
  height: 100%
  overflow: hidden
  align-items: stretch

.campaign-col-left
  height: 100%
  overflow-y: auto
  overflow-x: hidden
  padding-right: 8px

.campaign-col-right
  height: 100%
  overflow-y: auto
  overflow-x: hidden

.business-rules-list
  display: flex
  flex-direction: column
  gap: 8px

.business-rule-item
  display: flex
  align-items: flex-start
  gap: 12px
  padding: 12px
  background-color: #f8fafc
  border: 1px solid #e8ecf0
  border-radius: 8px

  .rule-icon
    flex-shrink: 0
    margin-top: 2px

  .rule-text
    margin: 0
    font-size: 13px
    color: #2c3f50
    line-height: 1.5
</style>
